<script>
  import { onMount } from 'svelte';
  import { querystring } from 'svelte-spa-router';
  import { api } from '../lib/api.js';
  export let params = {};

  let data = null, loading = true, error = null, showTop = false;

  // ---- Date range: same presets as the Analytics overview, including "since <milestone>" ----
  // Default is the whole history (the page's original behaviour). The overview passes its current
  // range through the link's query string so a "since …" view survives the click-through.
  const dayStr = (offset) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - offset); return d.toISOString().slice(0, 10); };
  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
  const ALL_FROM = '2026-06-01';
  const STORE_SINCE = '2026-08-30';
  const qs = new URLSearchParams($querystring || '');
  let rangeFrom = DAY_RE.test(qs.get('from') || '') ? qs.get('from') : ALL_FROM;
  let rangeTo = DAY_RE.test(qs.get('to') || '') ? qs.get('to') : dayStr(1);
  const basePresets = [
    { label: '7 days', from: dayStr(7) }, { label: '28 days', from: dayStr(28) }, { label: '90 days', from: dayStr(90) },
    { label: 'since full attribution (30 Aug)', from: STORE_SINCE }, { label: 'all', from: ALL_FROM },
  ];
  $: presets = [...basePresets, ...((data && data.milestones) || []).map((m) => ({ label: `since ${m.detail.length > 22 ? m.detail.slice(0, 21) + '…' : m.detail} (${fmtDate(m.date)})`, from: m.date, milestone: true }))];
  $: isAll = rangeFrom === ALL_FROM && rangeTo === dayStr(1);
  function setRange(from, to) { rangeFrom = from; rangeTo = to || dayStr(1); load(false); }

  onMount(() => load(false));
  async function load(refresh) {
    loading = true; error = null;
    const range = isAll ? '' : `&from=${rangeFrom}&to=${rangeTo}`;
    try { data = await api(`/analytics/keywords/${params.id}?${refresh ? 'refresh=1' : ''}${range}`); }
    catch (e) { error = e.message; } finally { loading = false; }
  }
  const money = (v) => (v == null ? '—' : `£${Number(v).toFixed(2)}`);
  const pct = (v, d = 0) => (v == null ? '—' : `${Number(v).toFixed(d)}%`);
  const pct1 = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
  const fmtTime = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

  // ---- timeline geometry (inline SVG, no chart lib) ----
  const W = 960, H = 240, PAD = { l: 44, r: 12, t: 14, b: 26 };
  $: days = data ? data.days.filter((d) => d.source === 'bing' || d.storeOrders) : [];
  $: xs = days.map((d) => d.date);
  $: x = (i) => PAD.l + (xs.length > 1 ? (i * (W - PAD.l - PAD.r)) / (xs.length - 1) : 0);
  $: bidMax = Math.max(0.2, ...days.flatMap((d) => [d.currentMaxCpc, d.firstPageBid, d.mainlineBid, d.avgCpc, showTop ? d.mainline1Bid : 0].filter((v) => v != null)));
  $: yBid = (v) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / bidMax);
  const path = (field, y) => days.map((d, i) => (d[field] == null ? null : `${x(i).toFixed(1)},${y(d[field]).toFixed(1)}`)).reduce((acc, p, i, arr) => acc + (p == null ? '' : (i > 0 && arr[i - 1] != null ? 'L' : 'M') + p), '');
  $: pBid = path('currentMaxCpc', yBid);
  $: pFirst = path('firstPageBid', yBid);
  $: pMain = path('mainlineBid', yBid);
  $: pTop = showTop ? path('mainline1Bid', yBid) : '';
  $: ticks = xs.filter((_, i) => i % Math.max(1, Math.round(xs.length / 8)) === 0 || i === xs.length - 1);
  $: yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => +(f * bidMax).toFixed(2));
  // second chart: impression share + quality score
  $: yPct = (v) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / 100);
  $: pIS = days && path('impressionShare', yPct);
  $: pTopShare = days && path('topShare', yPct);
  $: pQS = days && path('qualityScore', (v) => yPct(v * 10));
  $: estimatesFrom = (() => { const d = days.find((x) => x.firstPageBid != null); return d ? fmtDate(d.date) : null; })();
  // Milestone markers: nearest day index at or after the milestone date.
  $: msMarks = data ? (data.milestones || []).map((m) => { const i = xs.findIndex((d) => d >= m.date); return i < 0 ? null : { ...m, x: x(i) }; }).filter(Boolean) : [];
  $: eventsByDate = data ? data.events.reduce((m, e) => { (m[e.date] = m[e.date] || []).push(e); return m; }, {}) : {};
  const markerEvents = (d) => (eventsByDate[d.date] || []).filter((e) => e.type !== 'store_order');
  const sevColor = { good: '#1c7a2e', warn: '#b26a00', error: '#b00020', info: '#2a5db0' };
  // competitor heatmap
  $: matrix = data ? data.auction.matrix : [];
  $: matrixDesc = [...matrix].reverse(); // heatmap reads newest → oldest, left to right
  $: domains = data ? data.auction.domains : [];
  const heat = (v) => (v == null ? 'transparent' : `rgba(74,125,189,${Math.min(1, 0.15 + v * 1.3)})`);
  // Heatmap metric (all from Bing auction insights, per competitor per day):
  const METRICS = {
    is: { label: 'impression share', help: 'share of our eligible auctions in which their ad showed' },
    above: { label: 'position above us', help: 'when both showed, how often they sat above us' },
    top: { label: 'top of page', help: 'share of their impressions in the mainline above results' },
    absTop: { label: 'absolute top', help: 'share of their impressions in slot 1' },
    outrank: { label: 'we outrank them', help: 'share of all our auctions that day in which our ad ranked above theirs, or showed when theirs did not — higher is better for us' },
  };
  let metric = 'is';
  const cell = (m, dom) => (m.kpis && m.kpis[dom] ? m.kpis[dom][metric] : (metric === 'is' ? m.shares[dom] : null));
  const youCell = (m) => (metric === 'is' ? m.you : metric === 'top' ? (m.youKpis && m.youKpis.top) : metric === 'absTop' ? (m.youKpis && m.youKpis.absTop) : null);
  const heatFor = (v) => (v == null ? 'transparent' : metric === 'above' ? `rgba(178,106,0,${Math.min(1, 0.12 + v * 1.1)})` : metric === 'outrank' ? `rgba(28,122,46,${Math.min(1, 0.12 + v * 1.1)})` : heat(v));
</script>

<div class="head">
  <a href="#/analytics" class="back">← Analytics</a>
  {#if data}
    <h2>{data.keyword.text} <span class="chip">{data.keyword.matchType}</span>
      {#if !data.keyword.present}<span class="chip off">deleted</span>{:else if data.keyword.status !== 'Active' || data.keyword.adGroupStatus !== 'Active'}<span class="chip off">{data.keyword.status !== 'Active' ? data.keyword.status : 'group ' + data.keyword.adGroupStatus}</span>{/if}
    </h2>
    <span class="meta">{data.keyword.campaignName} › {data.keyword.adGroupName} · computed {fmtTime(data.computedAt)}{data.cached ? ' (cached)' : ''}</span>
    <button class="refresh" on:click={() => load(true)} disabled={loading}>Refresh</button>
  {/if}
</div>

<div class="rangebar">
  <label>From <input type="date" bind:value={rangeFrom} min={ALL_FROM} max={rangeTo} on:change={() => load(false)} /></label>
  <label>To <input type="date" bind:value={rangeTo} min={rangeFrom} max={dayStr(0)} on:change={() => load(false)} /></label>
  <span class="presets">{#each presets as p}<button class:on={rangeFrom === p.from && rangeTo === dayStr(1)} class:ms={p.milestone} title={p.milestone ? 'milestone' : ''} on:click={() => setRange(p.from)}>{p.milestone ? '📌 ' : ''}{p.label}</button>{/each}</span>
  {#if loading && data}<span class="busy">updating…</span>{/if}
</div>

{#if loading && !data}
  <p>Loading keyword history…</p>
{:else if error}
  <p class="error">{error}</p>
{:else if data}
  {@const k = data.keyword}
  {@const l = k.latest || {}}
  <div class="cards">
    <div class="card"><span class="label">Our bid</span><span class="value">{money(k.bid)}</span><span class="sub">{k.bidSource === 'adgroup' ? 'ad-group default' : 'keyword bid'}{k.belowFirstPage ? ' · below first-page estimate' : ''}</span></div>
    <div class="card"><span class="label">First page / mainline / top</span><span class="value">{money(l.firstPageBid)} <small>/ {money(l.mainlineBid)} / {money(l.mainline1Bid)}</small></span><span class="sub">Bing estimates as of {fmtDate(l.date)}</span></div>
    <div class="card"><span class="label">Impression share</span><span class="value">{pct(l.impressionShare)}</span><span class="sub">lost to rank {pct(l.lostToRank)} · top-of-page rate {pct(l.topRate)}</span></div>
    <div class="card"><span class="label">Quality score</span><span class="value">{l.qualityScore ?? '—'}</span><span class="sub">expected CTR {l.expectedCtr ?? '—'} / 3</span></div>
    <div class="card"><span class="label">28 days</span><span class="value">{money(k.d28 && k.d28.spend)}</span><span class="sub">{k.d28 ? `${k.d28.clicks} clicks · ${k.d28.storeOrders} store orders · CPO ${money(k.d28.cpo)} · Bing counted ${k.d28.bingConversions}` : '—'}</span></div>
  </div>

  <h3>By weekday <small>per-day averages over {isAll ? "the keyword's whole history" : 'the selected range'} · store orders by order date</small></h3>
  <div class="tablewrap">
      <table class="wk">
        <thead><tr><th>day</th><th class="num">days</th><th class="num">impr/day</th><th class="num">clicks/day</th><th class="num">CPC</th><th class="num">impr share</th><th class="num">orders/day</th><th class="num">CVR</th><th class="num">CPO</th><th class="num">revenue/day</th><th class="num">ROAS</th></tr></thead>
        <tbody>
          {#each data.weekday as r}
            <tr class:weekend={r.day === 'Sat' || r.day === 'Sun'}>
              <td>{r.day}</td><td class="num">{r.days}</td><td class="num">{r.impressionsPerDay ?? '—'}</td><td class="num">{r.clicksPerDay ?? '—'}</td><td class="num">{r.cpc != null ? '£' + r.cpc.toFixed(2) : '—'}</td><td class="num">{r.impressionShare != null ? r.impressionShare + '%' : '—'}</td>
              <td class="num" class:good={r.ordersPerDay > 0}>{r.ordersPerDay ?? '—'}</td><td class="num">{r.cvr != null ? r.cvr + '%' : '—'}</td><td class="num" class:bad={r.cpo != null && r.cpo > 8} class:good={r.cpo != null && r.cpo < 5}>{r.cpo != null ? '£' + r.cpo.toFixed(2) : '—'}</td><td class="num">{r.revenuePerDay != null ? '£' + r.revenuePerDay.toFixed(2) : '—'}</td><td class="num">{r.roas ?? '—'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
  </div>

  <h3>Bid vs Bing's estimates <small>daily · {xs.length} days</small>
    <label class="toggle"><input type="checkbox" bind:checked={showTop} /> show top-of-page estimate</label>
  </h3>
  <div class="chartbox">
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" class="chart">
      {#each yTicks as t}
        <line x1={PAD.l} x2={W - PAD.r} y1={yBid(t)} y2={yBid(t)} class="grid" />
        <text x={PAD.l - 6} y={yBid(t) + 4} class="ylab">£{t.toFixed(2)}</text>
      {/each}
      {#each ticks as t}<text x={x(xs.indexOf(t))} y={H - 8} class="xlab">{fmtDate(t)}</text>{/each}
      {#each msMarks as m}<line x1={m.x} x2={m.x} y1={PAD.t} y2={H - PAD.b} class="ms" /><text x={m.x + 4} y={PAD.t + 10} class="mslab">📌 {m.detail}</text>{/each}
      <path d={pTop} class="line top" />
      <path d={pMain} class="line main" />
      <path d={pFirst} class="line first" />
      <path d={pBid} class="line bid" />
      {#each days as d, i}
        {#if d.avgCpc != null}<circle cx={x(i)} cy={yBid(d.avgCpc)} r="2.2" class="dot cpc"><title>{d.date}: avg CPC £{d.avgCpc.toFixed(2)}, {d.clicks} clicks, £{(d.spend || 0).toFixed(2)}</title></circle>{/if}
        {#if d.storeOrders}<circle cx={x(i)} cy={PAD.t + 4} r={3 + Math.min(4, d.storeOrders)} class="dot order"><title>{d.date}: {d.storeOrders} store order{d.storeOrders > 1 ? 's' : ''} £{(d.storeRevenue || 0).toFixed(2)}{d.bingConversions ? '' : ' (Bing counted 0)'}</title></circle>{/if}
        {#each markerEvents(d) as e, j}
          <polygon points={`${x(i) - 4},${H - PAD.b - 2 - j * 8} ${x(i) + 4},${H - PAD.b - 2 - j * 8} ${x(i)},${H - PAD.b - 9 - j * 8}`} fill={sevColor[e.severity] || '#999'} opacity=".85"><title>{e.date}: {e.detail}</title></polygon>
        {/each}
      {/each}
    </svg>
    <div class="legend">
      <span><i class="sw bid"></i> our max CPC</span><span><i class="sw first"></i> first-page estimate</span><span><i class="sw main"></i> mainline estimate</span>{#if showTop}<span><i class="sw top"></i> top-of-page estimate</span>{/if}<span><i class="sw cpcdot"></i> actual avg CPC</span><span><i class="sw orderdot"></i> store order</span><span>▲ event (hover)</span>
    </div>
    <p class="chartnote">Bing only reports its bid estimates and our max CPC as of the moment a report runs, so those lines start on {estimatesFrom || '8 Sept 2026'} and grow one day at a time. Quality score, impression share, actual CPC, orders and events are true history.</p>
  </div>

  <h3>Impression share &amp; quality score</h3>
  <div class="chartbox">
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" class="chart">
      {#each [0, 25, 50, 75, 100] as t}
        <line x1={PAD.l} x2={W - PAD.r} y1={yPct(t)} y2={yPct(t)} class="grid" />
        <text x={PAD.l - 6} y={yPct(t) + 4} class="ylab">{t}%</text>
      {/each}
      {#each ticks as t}<text x={x(xs.indexOf(t))} y={H - 8} class="xlab">{fmtDate(t)}</text>{/each}
      {#each msMarks as m}<line x1={m.x} x2={m.x} y1={PAD.t} y2={H - PAD.b} class="ms" /><text x={m.x + 4} y={PAD.t + 10} class="mslab">📌 {m.detail}</text>{/each}
      <path d={pTopShare} class="line topshare" />
      <path d={pIS} class="line is" />
      <path d={pQS} class="line qs" />
    </svg>
    <div class="legend"><span><i class="sw is"></i> impression share</span><span><i class="sw topshare"></i> top-of-page share</span><span><i class="sw qs"></i> quality score (×10)</span></div>
  </div>

  <h3>Who was in the auction <small>per competitor by day, newest on the left · data through {matrix.length ? fmtDate(matrix[matrix.length - 1].date) : '—'} (Bing lags ~a week)</small>
    <span class="metricpick">{#each Object.entries(METRICS) as [k, m]}<button class:on={metric === k} title={m.help} on:click={() => (metric = k)}>{m.label}</button>{/each}</span>
  </h3>
  <p class="chartnote">{METRICS[metric].help}. Impression shares don't add to 100% — several ads show per page. "Position above us" (their side: when both showed, how often they were higher) and "we outrank them" (our side: across all our auctions) are relative to our ad, so our own row is blank for them.</p>
  {#if !matrix.length}
    <p class="empty">No auction insight data for this keyword — Bing withholds it below an impression threshold.</p>
  {:else}
    <div class="heatwrap">
      <table class="heat">
        <thead><tr><th>domain</th>{#each matrixDesc as m}<th class="hcol"><span>{fmtDate(m.date)}</span></th>{/each}</tr></thead>
        <tbody>
          {#key metric}
          <tr class="you"><td>cardhero.com (you)</td>{#each matrixDesc as m}{@const v = youCell(m)}<td title={`${m.date}: ${pct1(v)}`} style={`background:${v == null ? 'transparent' : `rgba(28,122,46,${Math.min(1, 0.15 + v * 1.3)})`}`}>{v != null ? Math.round(v * 100) : ''}</td>{/each}</tr>
          {#each domains as dom}
            <tr><td>{dom}</td>{#each matrixDesc as m}{@const v = cell(m, dom)}{@const k = m.kpis && m.kpis[dom]}<td title={k ? `${m.date} · ${dom}: share ${pct1(k.is)} · above us ${pct1(k.above)} · top of page ${pct1(k.top)} · abs top ${pct1(k.absTop)} · we outrank them ${pct1(k.outrank)}` : `${m.date}: ${dom} ${pct1(v)}`} style={`background:${heatFor(v)}`}>{v != null ? Math.round(v * 100) : ''}</td>{/each}</tr>
          {/each}
          {/key}
        </tbody>
      </table>
    </div>
  {/if}

  <h3>Competitor pressure vs our results <small>attributed days only ({data.pressureMeta.from} → {data.pressureMeta.through || '—'}: {data.pressureMeta.days} auction days, {data.pressureMeta.orders} store orders) · each competitor's days split in half by their position-above rate</small></h3>
  {#if !data.pressure.length}
    <p class="empty">Not enough auction days with attribution yet for this keyword.</p>
  {:else}
    <div class="tablewrap">
    <table class="wk">
      <thead><tr><th>competitor</th><th>their pressure</th><th class="num">days</th><th class="num">above us</th><th class="num">our clicks/day</th><th class="num">CTR</th><th class="num">CPC</th><th class="num">orders</th><th class="num">orders/100 clicks</th><th class="num">CPO</th></tr></thead>
      <tbody>
        {#each data.pressure as p}
          {#each [['low', p.low], ['high', p.high]] as [name, h]}
            <tr class:weekend={name === 'high'}>
              <td>{name === 'low' ? p.domain : ''}</td><td>{name === 'low' ? 'least' : 'most'}</td><td class="num">{h.days}</td><td class="num">{h.avgAbove != null ? Math.round(h.avgAbove * 100) + '%' : '—'}</td><td class="num">{h.clicksPerDay ?? '—'}</td><td class="num">{h.ctr != null ? h.ctr + '%' : '—'}</td><td class="num">{h.cpc != null ? '£' + h.cpc.toFixed(2) : '—'}</td><td class="num">{h.orders}</td><td class="num" class:good={name === 'high' && p.high.ordersPer100Clicks != null && p.low.ordersPer100Clicks != null && p.high.ordersPer100Clicks >= p.low.ordersPer100Clicks} class:bad={name === 'high' && p.high.ordersPer100Clicks != null && p.low.ordersPer100Clicks != null && p.high.ordersPer100Clicks < p.low.ordersPer100Clicks * 0.7}>{h.ordersPer100Clicks ?? '—'}</td><td class="num">{h.cpo != null ? '£' + h.cpo.toFixed(2) : '—'}</td>
            </tr>
          {/each}
        {/each}
      </tbody>
    </table>
    </div>
    <p class="chartnote">Read the "most" row against the "least" row for the same competitor. A red orders-per-100-clicks means our conversion fell by 30%+ when they pressed hardest; fewer clicks with unchanged conversion means they cost volume, not quality. Halves of a few dozen days move by a handful of orders — read direction, not decimals.</p>
  {/if}

  {#if data.landscape}
  <h3>What more volume would cost <small>Bing's 7-day bid landscape ({data.landscape.window || ''}), captured {fmtTime(data.landscape.at)} · estimates, not guarantees</small></h3>
  <div class="tablewrap">
  <table class="wk">
    <thead><tr><th>bid</th><th class="num">impressions/wk</th><th class="num">top impressions</th><th class="num">clicks/wk</th><th class="num">cost/wk</th><th class="num">CPC</th></tr></thead>
    <tbody>{#each data.landscape.points as pt}<tr class:weekend={data.landscape.currentBid != null && Math.abs(pt.bid - data.landscape.currentBid) < 0.005}><td>£{pt.bid.toFixed(2)}{data.landscape.currentBid != null && Math.abs(pt.bid - data.landscape.currentBid) < 0.005 ? ' ← current' : ''}</td><td class="num">{pt.impressions}</td><td class="num">{pt.topImpressions}</td><td class="num">{pt.clicks}</td><td class="num">£{pt.cost.toFixed(2)}</td><td class="num">{pt.cpc != null ? '£' + pt.cpc.toFixed(2) : '—'}</td></tr>{/each}</tbody>
  </table>
  </div>
  {#if data.landscape.marginal}
    {@const m = data.landscape.marginal}
    <p class="chartnote">Next step up, £{m.fromBid.toFixed(2)} → £{m.toBid.toFixed(2)}: about {m.extraClicks} more clicks a week for £{m.extraCost.toFixed(2)} more, a marginal CPC of {m.marginalCpc != null ? '£' + m.marginalCpc.toFixed(2) : '—'}{data.storeCvr && m.marginalCpc ? ` — at this keyword's ${data.storeCvr}% store CVR since ${data.pressureMeta.from} that is about £${(m.marginalCpc / (data.storeCvr / 100)).toFixed(0)} per extra order` : ''}.</p>
  {/if}
  {/if}

  <h3>What the results page showed <small>snapshots from a UK vantage point · desktop + mobile · newest first{isAll ? '' : ' · selected range'}</small></h3>
  {#if !data.serp.length}
    <p class="empty">No snapshots yet for this keyword.</p>
  {:else}
    <div class="snaps">
      {#each data.serp.slice(0, 12) as s}
        <div class="snap" class:us={s.ourSlot}>
          <div class="snaphead"><b>{fmtTime(s.at)}</b> <span class="chip">{s.device}</span> {#if s.ourSlot}<span class="chip on">us: {s.ourSlot}</span>{:else}<span class="chip off">not served to this cold visitor{s.page2Checked ? ' (pages 1–2)' : ''}</span>{/if}{#if s.vantage}<small class="grp">{s.vantage}</small>{/if}</div>
          {#if s.adCount}
            <ol class="adlist">
              {#each s.ads as a}<li class:ours={a.domain && a.domain.endsWith('cardhero.com')} class:p2={a.page === 2}><span class="blk">{a.page === 2 ? 'page 2 · ' : ''}{a.block} {a.slot}</span> <b>{a.domain || '?'}</b> — {a.headline}{#if a.description}<br /><small>{a.description}</small>{/if}{#if a.extensions && a.extensions.length}<br /><small class="ext">{a.extensions.join(' · ')}</small>{/if}</li>{/each}
            </ol>
          {:else}<p class="empty">No ads on the page.</p>{/if}
          {#if s.screenshot}<a class="shot" href={`/api/analytics/keywords/${data.keyword.id}/serp/${s.id}/screenshot`} target="_blank" rel="noopener">screenshot ↗</a>{/if}
          {#if s.organic.length}<small class="org">organic: {s.organic.map((o) => o.domain).join(', ')}</small>{/if}
        </div>
      {/each}
    </div>
  {/if}

  <div class="two">
    <div>
      <h3>Search queries <small>{isAll ? 'last 60 days' : 'selected range'}, Bing's view</small></h3>
      {#if !data.queries.length}<p class="empty">No query data.</p>{:else}
      <table><thead><tr><th>query</th><th>match</th><th>impr</th><th>clicks</th><th>spend</th></tr></thead>
        <tbody>{#each data.queries as q}<tr><td>{q.query}</td><td>{q.delivered}</td><td>{q.impressions}</td><td>{q.clicks}</td><td>{money(q.spend)}</td></tr>{/each}</tbody></table>
      {/if}
    </div>
    <div>
      <h3>Store orders <small>from the store's own attribution</small></h3>
      {#if !data.orders.length}<p class="empty">No attributed orders yet.</p>{:else}
      <table><thead><tr><th>order</th><th>clicked</th><th>ordered</th><th>query</th><th>match</th><th>£</th><th>via</th></tr></thead>
        <tbody>{#each data.orders as o}<tr><td>{o.orderName}</td><td>{fmtTime(o.clickedAt)}</td><td>{fmtTime(o.orderedAt)}</td><td>{o.query || '—'}</td><td>{o.deliveredMatch || '?'}{o.keywordAmbiguous ? ' ⚠' : ''}</td><td>{money(o.revenue)}</td><td>{o.attribution}</td></tr>{/each}</tbody></table>
      {/if}
      <h3>Events</h3>
      {#if !data.events.length}<p class="empty">Nothing yet.</p>{:else}
      <ul class="events">{#each data.events.slice(0, 60) as e}<li><span class="d">{e.date}</span> <span class="sev" style={`color:${sevColor[e.severity] || '#666'}`}>●</span> {e.detail}</li>{/each}</ul>
      {/if}
    </div>
  </div>
{/if}

<style>
  .head { display: flex; align-items: baseline; gap: 1rem; flex-wrap: wrap; }
  .back { font-size: .85rem; color: #2a5db0; text-decoration: none; }
  .rangebar { display: flex; gap: .6rem; align-items: center; flex-wrap: wrap; margin: .4rem 0 .9rem; font-size: .82rem; }
  .rangebar input { font: inherit; padding: .15rem .3rem; }
  .presets { display: flex; gap: .25rem; flex-wrap: wrap; }
  .presets button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .2rem .55rem; border-radius: 999px; font-size: .76rem; }
  .presets button.ms { border-style: dashed; }
  .presets button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; }
  .busy { color: #888; font-size: .78rem; }
  h2 { margin: .5rem 0 1rem; }
  h3 { margin: 1.75rem 0 .5rem; display: flex; align-items: baseline; gap: .75rem; }
  h3 small { font-weight: 400; color: #888; font-size: .8rem; }
  .metricpick { margin-left: auto; display: flex; gap: .25rem; flex-wrap: wrap; }
  .metricpick button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .15rem .5rem; border-radius: 999px; font-size: .72rem; font-weight: 400; }
  .metricpick button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; }
  .toggle { font-weight: 400; font-size: .8rem; color: #666; margin-left: auto; }
  .chip { font-size: .7rem; padding: .1rem .5rem; border-radius: 999px; background: #e8f0fe; color: #2a5db0; vertical-align: middle; }
  .chip.off { background: #f0f0f0; color: #888; }
  .meta { font-size: .8rem; color: #888; }
  .refresh { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .25rem .7rem; border-radius: 6px; font-size: .8rem; }
  .error { color: #b00020; }
  .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: .75rem; margin: 1rem 0; }
  .card { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .8rem 1rem; display: flex; flex-direction: column; gap: .2rem; }
  .card .label { font-size: .78rem; color: #777; text-transform: uppercase; letter-spacing: .03em; }
  .card .value { font-size: 1.4rem; font-weight: 700; }
  .card .value small { font-size: .9rem; font-weight: 500; color: #666; }
  .card .sub { font-size: .78rem; color: #888; }
  .tablewrap { overflow-x: auto; }
  table.wk { font-size: .8rem; }
  table.wk td, table.wk th { padding: .3rem .5rem; white-space: nowrap; }
  table.wk tr.weekend td { background: #fafafa; }
  table.wk td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.wk td.good { color: #1c7a2e; font-weight: 600; }
  table.wk td.bad { color: #b00020; }
  .chartbox { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .6rem; }
  .chart { width: 100%; height: 240px; display: block; }
  .grid { stroke: #eee; stroke-width: 1; }
  .ms { stroke: #7a3fbd; stroke-width: 1.2; stroke-dasharray: 5 3; vector-effect: non-scaling-stroke; }
  .mslab { font-size: 10px; fill: #7a3fbd; }
  .ylab { font-size: 10px; fill: #888; text-anchor: end; }
  .xlab { font-size: 10px; fill: #888; text-anchor: middle; }
  .line { fill: none; stroke-width: 1.6; vector-effect: non-scaling-stroke; }
  .line.bid { stroke: #222; stroke-width: 2; }
  .line.first { stroke: #d9822b; }
  .line.main { stroke: #4a7dbd; }
  .line.top { stroke: #999; stroke-dasharray: 4 3; }
  .line.is { stroke: #4a7dbd; stroke-width: 2; }
  .line.topshare { stroke: #9bb8dc; }
  .line.qs { stroke: #7a3fbd; stroke-dasharray: 3 3; }
  .dot.cpc { fill: #d33; }
  .dot.order { fill: #1c7a2e; }
  .chartnote { font-size: .74rem; color: #888; margin: .4rem .2rem 0; }
  .legend { display: flex; flex-wrap: wrap; gap: 1rem; font-size: .75rem; color: #666; padding: .4rem .2rem 0; }
  .sw { display: inline-block; width: 14px; height: 3px; vertical-align: middle; margin-right: .3rem; }
  .sw.bid { background: #222; } .sw.first { background: #d9822b; } .sw.main { background: #4a7dbd; } .sw.top { background: #999; }
  .sw.is { background: #4a7dbd; } .sw.topshare { background: #9bb8dc; } .sw.qs { background: #7a3fbd; }
  .sw.cpcdot, .sw.orderdot { width: 8px; height: 8px; border-radius: 50%; }
  .sw.cpcdot { background: #d33; } .sw.orderdot { background: #1c7a2e; }
  .heatwrap { overflow-x: auto; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; }
  table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; overflow: hidden; font-size: .85rem; }
  th, td { text-align: left; padding: .4rem .6rem; border-bottom: 1px solid #eee; }
  th { background: #f7f7f7; font-size: .74rem; text-transform: uppercase; letter-spacing: .03em; color: #666; }
  .heat { border: 0; font-size: .7rem; }
  .heat td { padding: .15rem .25rem; text-align: center; min-width: 22px; color: #222; }
  .heat td:first-child, .heat th:first-child { text-align: left; white-space: nowrap; font-size: .78rem; position: sticky; left: 0; background: #fff; z-index: 1; }
  .heat .hcol { padding: 0; height: 52px; vertical-align: bottom; }
  .heat .hcol span { display: block; transform: rotate(-60deg); transform-origin: left bottom; white-space: nowrap; font-size: .62rem; text-transform: none; letter-spacing: 0; margin-left: 12px; }
  .heat .you td { font-weight: 600; }
  .snaps { display: grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: .75rem; }
  .snap { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .6rem .8rem; font-size: .8rem; }
  .snap.us { border-color: #9bd3a5; }
  .snaphead { display: flex; gap: .4rem; align-items: center; margin-bottom: .4rem; }
  .chip.on { background: #e2f5e5; color: #1c7a2e; }
  .adlist { margin: 0; padding-left: 1.2rem; }
  .adlist li { margin: .2rem 0; }
  .adlist li.ours { background: #f0faf1; }
  .adlist li.p2 { opacity: .65; }
  .adlist .blk { color: #999; font-size: .7rem; text-transform: uppercase; }
  .adlist small { color: #666; }
  .adlist .ext { color: #999; }
  .shot { display: inline-block; margin-top: .4rem; font-size: .78rem; color: #2a5db0; text-decoration: none; }
  .org { display: block; color: #999; margin-top: .3rem; }
  .two { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  @media (max-width: 1000px) { .two { grid-template-columns: 1fr; } }
  .events { list-style: none; padding: 0; margin: 0; font-size: .82rem; }
  .events li { padding: .25rem 0; border-bottom: 1px solid #eee; }
  .events .d { color: #888; font-variant-numeric: tabular-nums; margin-right: .3rem; }
  .empty { color: #888; font-style: italic; }
</style>
